/**
 * @file Custom Print Update Status modal component.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';

import withModal from '../../../../../../../components/global/modal/withModal';
import { CUSTOM_PRINT_UPDATE_STATUS } from '../../../../../../../constants/modals';
import Select from '../../../../../../../components/global/Select';
import {
  ORDER_STATUS,
  ORDER_STATUS_LABELS,
} from '../../../../../../../constants/pro-custom-prints';
import { getErrorString } from '../../../../../../../util/normalize-errors';
import Button from '../../../../../../../components/global/Button';
import Alert from '../../../../../../../components/global/Alert';

class PurchasedCustomPrintUpdateStatusModal extends Component {
  static propTypes = {
    purchasedCustomPrint: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      total_price: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.string,
      }),
      album: PropTypes.shape({
        id: PropTypes.string,
      }),
      account: PropTypes.shape({
        id: PropTypes.string,
      }),
      custom_print: PropTypes.shape({
        id: PropTypes.string,
      }),
    }).isRequired,
    purchasedCustomPrintUpdateMutation: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    const { purchasedCustomPrint } = props;

    this.state = {
      orderStatus: purchasedCustomPrint.order_status,
      error: '',
      submitting: false,
    };
  }

  onSubmit = () => {
    const {
      purchasedCustomPrint,
      purchasedCustomPrintUpdateMutation,
      onRequestClose,
    } = this.props;

    const { orderStatus, submitting } = this.state;

    if (submitting) {
      return;
    }

    if (!orderStatus) {
      this.setState(() => ({
        error: 'A new order status is required to perform this action.',
      }));

      return;
    }

    this.setState(
      () => ({ submitting: true }),
      async () => {
        try {
          await purchasedCustomPrintUpdateMutation(
            purchasedCustomPrint.id,
            orderStatus,
            purchasedCustomPrint,
          );

          onRequestClose();
        } catch (error) {
          this.setState(() => ({ error: getErrorString(error) }));
        }
      },
    );
  };

  onInputChange = event => {
    const {
      target: { name, value },
    } = event;

    this.setState(() => ({
      [name]: value,
    }));
  };

  render() {
    const { purchasedCustomPrint, onRequestClose } = this.props;
    const { orderStatus, error, submitting } = this.state;

    return (
      <div>
        <p>
          You are about to modify the status of this order. Please review with
          care.
        </p>
        {error && <Alert type="danger">{error}</Alert>}
        <p>
          <span className="d-block label">Current Status: </span>
          <span className="text-xs text-capitalize text-bold text-dark">
            {purchasedCustomPrint.order_status}
          </span>
        </p>
        <Select
          id="purchased-custom-prints-order-status"
          className="ml-auto"
          name="orderStatus"
          label="New Status:"
          value={orderStatus === ORDER_STATUS.CUSTOMER_INPUT ? '' : orderStatus}
          required
          disabled={submitting}
          onChange={this.onInputChange}
        >
          {[
            ORDER_STATUS.PENDING,
            ORDER_STATUS.SHIPPED,
            ORDER_STATUS.CANCELED,
          ].map(status => (
            <option value={status} key={status}>
              {ORDER_STATUS_LABELS[status]}
            </option>
          ))}
        </Select>

        <div className="mt-1">
          <Button
            onClick={this.onSubmit}
            disabled={submitting}
            btnBlock
            className="mb-1"
          >
            {submitting ? 'Loading...' : 'Update order status'}
          </Button>
          <Button
            btnBlock
            btnStyle="secondary"
            disabled={submitting}
            onClick={onRequestClose}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }
}

export default withModal(PurchasedCustomPrintUpdateStatusModal, {
  name: CUSTOM_PRINT_UPDATE_STATUS,
  contentLabel: 'Change Order Status',
  size: 'sm',
  dismissible: true,
});
